import React, { useContext } from "react";
import { connect } from "react-redux";
import { Heading } from "components/Heading";
import {
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { useSnackbar } from "notistack";
import { useMutate } from "restful-react";

import { UserContext, IUserContext } from "context/user";
import { TextField } from "components/TextField";
import { AsyncSelectField } from "components/SelectField";
import { startLoadingAction, stopLoadingAction } from "components/ReduxSpinner";
import { debounce } from "components/debounce";
import { routing } from "routing";
import {
  searchTeam,
  ISearchTeamNameInput,
  ITeamsResponse,
  ICreateMatchInput,
  ITeam,
  createMatch,
  ICreateMatchResponse,
} from "apis";
import { getLocalDateTimeString, parseTimestamp } from "utils/datetime";
import { ISelectOption } from "types";

import { CreateMatchCompProps } from "./types";
import {
  FIELD_ADDRESS,
  FIELD_DESCRIPTION,
  FIELD_START_TIME,
  FIELD_PLAYER_NUM,
  FIELD_HOME_TEAM,
  FIELD_AWAY_TEAM,
  DEFAULT_PLAYER_NUM,
} from "./constants";

const { Urls } = routing;

const buildTeamObj = (teamOption: null | ISelectOption): ITeam => {
  if (!teamOption) {
    return { id: -1, name: "-" };
  }

  return {
    id: teamOption.value,
    name: teamOption.label,
  };
};

const CreateMatchComp = ({
  startLoading,
  stopLoading,
}: CreateMatchCompProps) => {
  const history = useHistory();
  const { defaultTeam, teams }: IUserContext = useContext(UserContext);

  const { enqueueSnackbar } = useSnackbar();

  const loadTeamOptions = debounce((name: string, callback: any) => {
    if (!name) {
      callback(
        teams.map((team) => ({
          label: team.name,
          value: team.id,
        }))
      );
      return;
    }

    const input: ISearchTeamNameInput = {
      name: `%${name}%`,
    };
    mutateSearchTeam(input).then((response: ITeamsResponse) => {
      if (response.errCode > 0 || response.teams.length === 0) {
        return callback();
      }
      callback(
        response.teams.map((team) => ({ label: team.name, value: team.id }))
      );
    });
  });

  const { mutate: mutateSearchTeam } = useMutate({
    verb: "POST",
    path: searchTeam(),
  });

  const { mutate: mutateCreateMatch } = useMutate({
    verb: "POST",
    path: createMatch(),
  });

  const handleCancel = () => {
    history.push(Urls.CALENDAR());
  };

  return (
    <>
      <Heading
        title={intl.get("CALENDAR_CREATE_MATCH")}
        rightAction={
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
          >
            {intl.get("FORM_GENERAL_CANCEL")}
          </Button>
        }
      />
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Formik
                enableReinitialize
                initialValues={{
                  [FIELD_ADDRESS]: "",
                  [FIELD_DESCRIPTION]: "",
                  [FIELD_START_TIME]: getLocalDateTimeString(),
                  [FIELD_PLAYER_NUM]: DEFAULT_PLAYER_NUM,
                  [FIELD_HOME_TEAM]: {
                    label: teams.find((team) => team.id === defaultTeam)?.name,
                    value: defaultTeam,
                  },
                  [FIELD_AWAY_TEAM]: null,
                }}
                onSubmit={async (values: any) => {
                  startLoading();

                  if (!(values[FIELD_HOME_TEAM] || values[FIELD_AWAY_TEAM])) {
                    enqueueSnackbar(
                      intl.get(
                        "CREATE_MATCH_VALIDATION_AT_LEAST_ONE_TEAM_VALID"
                      ),
                      {
                        variant: "warning",
                      }
                    );
                    return;
                  }

                  const input: ICreateMatchInput = {
                    address: values[FIELD_ADDRESS],
                    description: values[FIELD_DESCRIPTION],
                    startTime: parseTimestamp(values[FIELD_START_TIME]),
                    homeTeam: buildTeamObj(values[FIELD_HOME_TEAM]),
                    awayTeam: buildTeamObj(values[FIELD_AWAY_TEAM]),
                  };

                  mutateCreateMatch(input)
                    .then((response: ICreateMatchResponse) => {
                      if (response.errCode > 0) {
                        enqueueSnackbar(response.errMessage, {
                          variant: "error",
                        });
                        return;
                      }

                      enqueueSnackbar(intl.get("CREATE_MATCH_SUCCESS"), {
                        variant: "success",
                      });

                      history.push(Urls.CALENDAR());
                    })
                    .catch(() => {
                      enqueueSnackbar(intl.get("CREATE_MATCH_FAILED"), {
                        variant: "error",
                      });
                    })
                    .finally(() => stopLoading());
                }}
              >
                {() => {
                  return (
                    <Form>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <Typography gutterBottom>
                            <strong>
                              {intl.get("CREATE_MATCH_FIELD_HOME_TEAM")}
                            </strong>
                          </Typography>
                          <Field
                            component={AsyncSelectField}
                            name={FIELD_HOME_TEAM}
                            loadOptions={loadTeamOptions}
                            defaultOptions
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <Typography gutterBottom>
                            <strong>
                              {intl.get("CREATE_MATCH_FIELD_AWAY_TEAM")}
                            </strong>
                          </Typography>
                          <Field
                            component={AsyncSelectField}
                            name={FIELD_AWAY_TEAM}
                            loadOptions={loadTeamOptions}
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <Typography gutterBottom>
                            <strong>
                              {intl.get("CREATE_MATCH_FIELD_TIME")}
                            </strong>
                          </Typography>
                          <Field
                            component={TextField}
                            type="datetime-local"
                            name={FIELD_START_TIME}
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <Typography gutterBottom>
                            <strong>
                              {intl.get("CREATE_MATCH_FIELD_LOCATION")}
                            </strong>
                          </Typography>
                          <Field component={TextField} name={FIELD_ADDRESS} />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <Typography gutterBottom>
                            <strong>{intl.get("TEAM_EDIT_DESCRIPTION")}</strong>
                          </Typography>
                          <Field
                            component={TextField}
                            multiline
                            rows="4"
                            name={FIELD_DESCRIPTION}
                          />
                          <Box mt={2} />
                        </Grid>
                      </Grid>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        size="large"
                      >
                        {intl.get("TEAM_SUBMIT")}
                      </Button>
                    </Form>
                  );
                }}
              </Formik>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const mapDispatch = {
  startLoading: () => startLoadingAction,
  stopLoading: () => stopLoadingAction,
};

export const CreateMatch = connect(null, mapDispatch)(CreateMatchComp);
